import React, { useState, useEffect } from 'react';
import useBackdropClose from '../../hooks/useBackdropClose';
import DatePicker, { registerLocale } from 'react-datepicker';
import { sv } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import TimeInput from './TimeInput';

// Registrera svenska språket för DatePicker
registerLocale('sv', sv);

/**
 * Hjälpfunktion för att beräkna sluttid baserat på starttid
 * @param {string} startTime - Starttid i format "HH:MM"
 * @param {number} durationMinutes - Möteslängd i minuter (default: 60)
 * @returns {string} - Sluttid i format "HH:MM"
 */
const calculateEndTime = (startTime, durationMinutes = 60) => {
  if (!startTime) return '10:00';
  const [hours, minutes] = startTime.split(':').map(Number);
  const totalMinutes = hours * 60 + minutes + durationMinutes;
  const endHours = Math.floor(totalMinutes / 60) % 24;
  const endMinutes = totalMinutes % 60;
  return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
};

/**
 * SuggestTimeModal - Generisk modal för att föreslå tidpunkter
 * Kan användas för estimat-uppföljning, hembesök, offertuppföljning, etc.
 *
 * @param {boolean} show - Om modalen ska visas
 * @param {function} onClose - Callback när modalen stängs
 * @param {function} onSubmit - Callback när förslag skickas: (proposals, message) => Promise
 * @param {string} title - Rubrik för modalen
 * @param {string} description - Beskrivning av vad förslagen används till
 * @param {number} maxProposals - Max antal förslag (default: 3)
 * @param {Array} timeSlots - Tillgängliga tidsslots, t.ex. ['11-12', '14-15', '16-17']
 * @param {string} mode - 'flexible' (datepicker) eller 'pregenerated' (checkboxar)
 * @param {Array} pregeneratedSlots - Fördefinierade tidpunkter för pregenerated mode
 * @param {string} submitButtonText - Custom text för submit-knappen (om null, använd default)
 * @param {boolean} allowCustomTime - Om användaren ska kunna ange egen tid utanför fasta slots (default: true för flexible mode)
 */
const SuggestTimeModal = ({
  show,
  onClose,
  onSubmit,
  title = 'Föreslå tider för uppföljning',
  description = 'Välj 2-3 tidsförslag som skickas till kunden. Inget möte bokas än - kunden får välja vilket förslag som passar bäst.',
  maxProposals = 3,
  timeSlots = ['11-12', '14-15'],
  mode = 'flexible', // 'flexible' eller 'pregenerated'
  pregeneratedSlots = [], // För pregenerated mode
  submitButtonText = null, // Custom text för submit-knappen (om null, använd default)
  allowCustomTime = true // Tillåt egen tid i flexible mode
}) => {
  const [timeProposals, setTimeProposals] = useState([]);
  const [suggestDate, setSuggestDate] = useState(null);
  const [suggestBlock, setSuggestBlock] = useState(timeSlots[0] || '11-12');
  const [suggestedTimesText, setSuggestedTimesText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // För pregenerated mode - checkat state
  const [selectedPregenerated, setSelectedPregenerated] = useState([]);

  // För custom time i flexible mode — start+sluttid är standard när det är tillåtet
  const [useCustomTime, setUseCustomTime] = useState(allowCustomTime);
  const [customStartTime, setCustomStartTime] = useState('09:00');
  const [customEndTime, setCustomEndTime] = useState('10:00');

  // Kolla om mobil (för DatePicker withPortal)
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 640);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-uppdatera sluttid när starttid ändras (+1 timme som default)
  useEffect(() => {
    if (useCustomTime && customStartTime) {
      setCustomEndTime(calculateEndTime(customStartTime, 60));
    }
  }, [customStartTime, useCustomTime]);

  // Backdrop-stängning (före tidig return — rules of hooks). handleClose har egna guards.
  const backdrop = useBackdropClose(() => handleClose());

  if (!show) return null;

  const handleAddProposal = () => {
    if (!suggestDate) {
      alert('Välj ett datum först.');
      return;
    }

    // Validera custom tid om det är valt
    if (useCustomTime) {
      if (!customStartTime || !customEndTime) {
        alert('Välj både start- och sluttid.');
        return;
      }
      if (customStartTime >= customEndTime) {
        alert('Sluttiden måste vara efter starttiden.');
        return;
      }
    }

    // Använd robust datumformatering för att undvika tidszons-problem
    const year = suggestDate.getFullYear();
    const month = String(suggestDate.getMonth() + 1).padStart(2, '0');
    const day = String(suggestDate.getDate()).padStart(2, '0');
    const weekdays = ['sön', 'mån', 'tis', 'ons', 'tor', 'fre', 'lör'];
    const weekday = weekdays[suggestDate.getDay()];
    const dateIso = `${year}-${month}-${day}`;

    let time, endTime, displayText;

    if (useCustomTime) {
      // Använd custom tid
      time = customStartTime;
      endTime = customEndTime;
      displayText = `${weekday} ${day}/${month} kl ${time}–${endTime}`;
    } else {
      // Fast slot — start och slut från sloten (t.ex. '11-12' -> 11:00–12:00)
      const [startHour, endHour] = suggestBlock.split('-');
      time = `${startHour}:00`;
      endTime = `${endHour}:00`;
      displayText = `${weekday} ${day}/${month} kl ${time}–${endTime}`;
    }

    // Kontrollera om förslaget redan finns
    if (timeProposals.some(p => p.displayText === displayText)) {
      alert('Detta förslag finns redan.');
      return;
    }

    setTimeProposals(prev => [...prev, {
      date: dateIso,
      time,
      endTime,
      displayText
    }]);
    setSuggestDate(null);
  };

  const handleRemoveProposal = (index) => {
    setTimeProposals(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    let proposalsToSubmit = [];
    
    if (mode === 'pregenerated') {
      if (selectedPregenerated.length === 0) {
        alert('Välj minst ett tidsförslag.');
        return;
      }
      // Konvertera pregenerated slots till proposal-format
      proposalsToSubmit = selectedPregenerated.map(slot => ({
        displayText: slot,
        date: '', // Behövs inte för pregenerated
        time: ''  // Behövs inte för pregenerated
      }));
    } else {
      if (timeProposals.length === 0) {
        alert('Lägg till minst ett tidsförslag först.');
        return;
      }
      proposalsToSubmit = timeProposals;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(proposalsToSubmit, suggestedTimesText);
      
      // Reset state
      setTimeProposals([]);
      setSelectedPregenerated([]);
      setSuggestedTimesText('');
      setSuggestDate(null);
      setSuggestBlock(timeSlots[0] || '11-12');
      
      onClose();
    } catch (error) {
      console.error('Fel vid skickning av tidsförslag:', error);
      // Låt anroparen hantera felmeddelanden
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      // Reset state när man stänger
      setTimeProposals([]);
      setSelectedPregenerated([]);
      setSuggestedTimesText('');
      setSuggestDate(null);
      setSuggestBlock(timeSlots[0] || '11-12');
      setUseCustomTime(allowCustomTime);
      setCustomStartTime('09:00');
      setCustomEndTime('10:00');
      onClose();
    }
  };
  
  const handleTogglePregenerated = (slot) => {
    if (selectedPregenerated.includes(slot)) {
      setSelectedPregenerated(selectedPregenerated.filter(s => s !== slot));
    } else {
      if (selectedPregenerated.length < maxProposals) {
        setSelectedPregenerated([...selectedPregenerated, slot]);
      }
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      {...backdrop}
    >
      <div
        className={`bg-white rounded-t-2xl sm:rounded-lg shadow-lg w-full p-5 sm:mx-4 max-h-[90vh] overflow-y-auto ${mode === 'pregenerated' ? 'sm:max-w-3xl' : 'sm:max-w-lg'}`}
        style={{ animation: isMobile ? 'slideUp 0.3s ease-out' : undefined }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-800">{title}</h3>
          <button
            className="text-gray-500 hover:text-gray-700"
            aria-label="Stäng"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="space-y-4">
          <p className="text-sm text-gray-600">{description}</p>
          
          {/* PREGENERATED MODE - Checkboxar */}
          {mode === 'pregenerated' && pregeneratedSlots.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">Välj 2-3 tider</label>
                <span className="text-xs text-gray-500">{selectedPregenerated.length}/{maxProposals} valda</span>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-80 overflow-y-auto">
                {pregeneratedSlots.map((slot, index) => (
                  <label 
                    key={index} 
                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedPregenerated.includes(slot)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    } ${
                      selectedPregenerated.length >= maxProposals && !selectedPregenerated.includes(slot)
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedPregenerated.includes(slot)}
                      onChange={() => handleTogglePregenerated(slot)}
                      disabled={selectedPregenerated.length >= maxProposals && !selectedPregenerated.includes(slot)}
                      className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{slot}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          
          {/* FLEXIBLE MODE - Datepicker */}
          {mode === 'flexible' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">Tidsförslag</label>
                <span className="text-xs text-gray-500">{timeProposals.length}/{maxProposals} förslag</span>
              </div>

              {/* Lista över valda förslag */}
              {timeProposals.length > 0 && (
                <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
                  {timeProposals.map((proposal, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <span>{proposal.displayText}</span>
                      <button
                        onClick={() => handleRemoveProposal(index)}
                        className="text-red-500 hover:text-red-700"
                        type="button"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Lägg till nytt förslag */}
              {timeProposals.length < maxProposals && (
                <div className="space-y-3">
                  {/* Custom tid toggle (om tillåtet) */}
                  {allowCustomTime && (
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="useCustomTimeProposal"
                        checked={useCustomTime}
                        onChange={(e) => setUseCustomTime(e.target.checked)}
                        className="w-4 h-4 text-leadme-pink border-gray-300 rounded focus:ring-leadme-pink"
                      />
                      <label htmlFor="useCustomTimeProposal" className="text-sm text-gray-700 cursor-pointer">
                        Använd egen tid (utanför fasta tidsblock)
                      </label>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Datum</label>
                      <DatePicker
                        selected={suggestDate}
                        onChange={(d) => setSuggestDate(d)}
                        dateFormat="EEE dd/MM"
                        minDate={new Date()}
                        locale="sv"
                        filterDate={(date) => {
                          const day = date.getDay(); // 0=Sun ... 6=Sat
                          return day !== 0 && day !== 6; // tillåt mån-fre
                        }}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                        calendarClassName="custom-datepicker"
                        placeholderText="Välj datum"
                        withPortal={isMobile}
                      />
                    </div>

                    {/* Visa antingen fasta tidsblock ELLER custom tid-inputs */}
                    {!useCustomTime ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tid</label>
                        <div className="flex gap-2">
                          <select
                            value={suggestBlock}
                            onChange={(e) => setSuggestBlock(e.target.value)}
                            className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                          >
                            {timeSlots.map((slot) => (
                              <option key={slot} value={slot}>
                                {slot.replace('-', ':00–')}:00
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={handleAddProposal}
                            type="button"
                            className="px-2.5 py-1.5 sm:px-3 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors whitespace-nowrap flex items-center gap-1"
                          >
                            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                            </svg>
                            Lägg till
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="col-span-1 sm:col-span-2 grid grid-cols-3 gap-3 items-end">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Starttid</label>
                          <TimeInput
                            value={customStartTime}
                            onChange={(e) => setCustomStartTime(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Sluttid</label>
                          <TimeInput
                            value={customEndTime}
                            onChange={(e) => setCustomEndTime(e.target.value)}
                          />
                        </div>
                        <div>
                          <button
                            onClick={handleAddProposal}
                            type="button"
                            className="w-full px-2.5 py-1.5 sm:px-3 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors whitespace-nowrap flex items-center justify-center gap-1"
                          >
                            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                            </svg>
                            Lägg till
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Anteckningar - ENDAST för flexible mode */}
          {mode === 'flexible' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Anteckningar (valfritt)
              </label>
              <textarea
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                rows={3}
                value={suggestedTimesText}
                onChange={(e) => setSuggestedTimesText(e.target.value)}
                placeholder="Lägg till anteckningar för mötet..."
              />
            </div>
          )}

          {/* Footer buttons */}
          <div className="flex items-center justify-end gap-2">
            <button
              className="px-2.5 py-1.5 sm:px-3 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              onClick={handleClose}
              type="button"
              disabled={isSubmitting}
            >
              Avbryt
            </button>
            <button
              className="px-2.5 py-1.5 sm:px-3 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
              disabled={(mode === 'pregenerated' ? selectedPregenerated.length === 0 : timeProposals.length === 0) || isSubmitting}
              onClick={handleSubmit}
              type="button"
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              {isSubmitting
                ? 'Skickar...'
                : submitButtonText
                  ? submitButtonText
                  : `Skicka förslag (${mode === 'pregenerated' ? selectedPregenerated.length : timeProposals.length})`
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuggestTimeModal;
